import type { Appointment } from 'src/features/appointments/types';
import type { SessionUser } from 'src/features/auth/types';
import { formatDate } from 'src/shared/utils/datetime';
import type { Payment, PaymentKind, PaymentMethod } from './types';

export const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo',
  transfer: 'Transferencia',
  card: 'Tarjeta',
  other: 'Otro',
};

export const METHOD_OPTIONS: PaymentMethod[] = ['cash', 'transfer', 'card', 'other'];

export const KIND_LABELS: Record<PaymentKind, string> = {
  payment: 'Cobro',
  refund: 'Reembolso',
};

/**
 * Whether this role may write money into a closed period.
 *
 * Mirrors `finance:backdate` on the API, which only the owner holds. The server
 * is still the one that decides; this only keeps the form from sending
 * something it already knows will bounce.
 */
export const canBackdate = (role: SessionUser['role'] | undefined): boolean => role === 'owner';

/** How far ahead of the clock a payment may be dated. Same allowance as the API. */
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

/**
 * Why the API would refuse this date for a payment or a refund, or `null`.
 *
 * Checked before sending so the reason reaches the user in Spanish and on the
 * date field, instead of as the API's English prose in a banner. `paidAt` is
 * the instant `zonedToInstant` built from the form, and `dateKey` the local
 * day it was built from — the cutoff is a business day, not an instant.
 */
export const paidAtProblem = (
  paidAt: string,
  dateKey: string,
  booksClosedThrough: string | null | undefined,
  mayBackdate: boolean,
  now: number = Date.now()
): string | null => {
  if (!paidAt) return 'Indica una fecha y una hora válidas';
  if (Date.parse(paidAt) > now + FUTURE_TOLERANCE_MS) {
    return 'No puede ser una fecha u hora futura';
  }
  if (!mayBackdate && booksClosedThrough && dateKey <= booksClosedThrough) {
    return `Los libros están cerrados hasta el ${formatDate(booksClosedThrough)}; solo la dueña puede registrar en esa fecha`;
  }
  return null;
};

/** What the date field says when the API refuses `paidAt` for a reason the form missed. */
export const PAID_AT_REFUSED = 'Revisa la fecha: no puede ser futura ni caer en un periodo cerrado';

/** Appointment statuses the API refuses to charge; mirrored so the UI does not offer it. */
export const isChargeable = (appointment: Pick<Appointment, 'status'>): boolean =>
  appointment.status !== 'cancelled' && appointment.status !== 'no_show';

/** What is left to give back of one payment, amount and tip apart. */
export const refundableOf = (payment: Payment): { amount: number; tip: number } => ({
  amount: round(payment.amount - payment.refundedAmount),
  tip: round(payment.tipAmount - payment.refundedTipAmount),
});

/**
 * Whether the refund button has anything to act on.
 *
 * Keyed on the amount alone, deliberately, even though a tip has a ceiling of
 * its own: the API refuses a refund of zero, so a payment whose price is fully
 * returned and whose tip is not would show a button that opens a form nothing
 * can submit. The tip travels with the amount — the refund form offers all of
 * it by default — and a tip left behind on purpose stays behind.
 */
export const isRefundable = (payment: Payment): boolean =>
  payment.kind === 'payment' && refundableOf(payment).amount > 0;

/**
 * What to put in the amount box when the payment form opens.
 *
 * The outstanding balance, except on an appointment nobody has paid anything on
 * yet whose services ask for a deposit — there, the deposit is what is being
 * collected, and it is the reason `requiresDeposit` has existed since S2
 * without a single reader.
 */
export const suggestedAmount = (appointment: Appointment, depositTotal: number): number => {
  const outstanding = appointment.balanceDue;
  if (appointment.paidAmount === 0 && depositTotal > 0 && depositTotal < outstanding) {
    return depositTotal;
  }
  return outstanding;
};

/**
 * How far a typed amount goes past what the appointment still owes, or 0.
 *
 * That difference is extra work the client asked for: the payment form adds
 * it to the appointment before charging, since the API refuses a payment above
 * the balance.
 */
export const extraOver = (amount: number, balanceDue: number): number =>
  Math.max(0, round(amount - balanceDue));

/**
 * Two decimals, always.
 *
 * Money arrives from the API as a JSON number, so subtracting two of them can
 * land on 4.999999999999999 — and that number, shown to somebody counting cash,
 * is worse than being a cent off.
 */
export const round = (value: number): number => Math.round(value * 100) / 100;
